import { useState, useRef, useEffect } from "react";
import { Table, TableBody, TableCell, TableFooter, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Plus, Trash2, Zap, FileEdit } from "lucide-react";
import { InvoiceItem } from "@/types/invoice";

interface EditingCell {
  id: number;
  field: keyof InvoiceItem;
  value: string;
}

interface EditableInvoiceTableProps {
  items: InvoiceItem[];
  currency: string;
  onUpdate: (id: number, field: keyof InvoiceItem, value: string) => void;
  onDelete: (id: number) => void;
  onAdd: () => void;
  readOnly?: boolean;
  pdfMode?: boolean;
  language?: string;
}

export function EditableInvoiceTable({
  items,
  currency,
  onUpdate,
  onDelete,
  onAdd,
  readOnly = false,
  pdfMode = false,
  language = 'en'
}: EditableInvoiceTableProps) {
  const [editingCell, setEditingCell] = useState<EditingCell | null>(null);
  const [editValue, setEditValue] = useState<string>("");
  const [pendingNewItemEdit, setPendingNewItemEdit] = useState<boolean>(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (pendingNewItemEdit && items.length > 0) {
      const newItem = items[items.length - 1];
      if (newItem) {
        startEditing(newItem.id, 'description', newItem.description || '');
        setPendingNewItemEdit(false);
      }
    }
  }, [pendingNewItemEdit, items]);
  
  const startEditing = (id: number, field: keyof InvoiceItem, value: any) => {
    if (readOnly) return;
    setEditingCell({
      id,
      field,
      value: String(value)
    });
    setEditValue(String(value));
  };
  
  const cancelEditing = () => {
    setEditingCell(null);
    setEditValue("");
  };
  
  const handleSave = (id: number, field: keyof InvoiceItem, value: string) => {
    onUpdate(id, field, value);
    cancelEditing();
  };
  
  const calculateTotal = () => {
    const itemsToCalculate = pdfMode ? 
      items.filter(item => item.description && item.description.trim() !== '') : 
      items;
    return itemsToCalculate.reduce((sum, item) => sum + (item.amount || 0), 0);
  };

  const getCurrencySymbol = (currencyCode: string) => {
    const currencies: Record<string, string> = {
      'USD': '$',
      'EUR': '€',
      'GBP': '£',
      'JPY': '¥',
      'CNY': '¥',
      'INR': '₹',
      'BRL': 'R$',
      'MXN': '$',
      'CAD': 'C$',
      'AUD': 'A$'
    };
    return currencies[currencyCode] || currencyCode;
  };
  
  const formatCurrency = (value: number | undefined) => {
    if (value === undefined || value === null || isNaN(value)) {
      return `${getCurrencySymbol(currency)}0`;
    }
    return `${getCurrencySymbol(currency)}${value.toLocaleString(undefined, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    })}`;
  };

  const handleTabNavigation = (e: React.KeyboardEvent<HTMLInputElement>, currentId: number, currentField: keyof InvoiceItem) => {
    if (e.key === 'Tab') {
      e.preventDefault();

      handleSave(currentId, currentField, editValue);

      const fieldOrder: (keyof InvoiceItem)[] = ['description', 'quantity', 'rate'];

      const currentFieldIndex = fieldOrder.indexOf(currentField);

      const currentItemIndex = items.findIndex(item => item.id === currentId);

      if (currentFieldIndex < fieldOrder.length - 1) {
        const nextField = fieldOrder[currentFieldIndex + 1];
        setTimeout(() => startEditing(currentId, nextField, items[currentItemIndex][nextField]), 10);
      } else if (currentItemIndex < items.length - 1) {
        const nextItemId = items[currentItemIndex + 1].id;
        setTimeout(() => startEditing(nextItemId, 'description', items[currentItemIndex + 1].description), 10);
      } else {
        onAdd();
        setPendingNewItemEdit(true);
      }
    }
  };

  const handleAddItem = () => {
    onAdd();
    setPendingNewItemEdit(true);
  };

  const displayItems = pdfMode ? 
    items.filter(item => item.description && item.description.trim() !== '') : 
    items;

  return <div className="relative">
      <Table className="text-sm">
        <TableHeader>
          <TableRow className="bg-gray-100">
            <TableHead className="py-1 px-4 text-left w-[50%]">Description</TableHead>
            <TableHead className="py-1 px-4 text-right w-[15%]">Quantity</TableHead>
            <TableHead className="py-1 px-4 text-right w-[20%]">Rate</TableHead>
            <TableHead className="py-1 px-4 text-right w-[15%]">Amount</TableHead>
            {!readOnly && !pdfMode && <TableHead className="w-10"></TableHead>}
          </TableRow>
        </TableHeader>
        <TableBody>
          {displayItems.map(item => <TableRow key={item.id} className="border-b">
              <TableCell className="py-2 px-4">
                {editingCell?.id === item.id && editingCell?.field === "description" ? <Input ref={inputRef} value={editValue} onChange={e => setEditValue(e.target.value)} className="h-7 w-full" onKeyDown={e => {
              if (e.key === "Enter") handleSave(item.id, "description", editValue);
              if (e.key === "Escape") cancelEditing();
              handleTabNavigation(e, item.id, "description");
            }} onBlur={() => handleSave(item.id, "description", editValue)} autoFocus /> : 
                <div className="flex flex-col">
                  <div className={`${!readOnly && !pdfMode ? "cursor-pointer hover:bg-accent hover:text-accent-foreground" : ""} p-2 rounded min-h-[2rem] flex items-center`} onClick={() => !pdfMode && startEditing(item.id, "description", item.description)}>
                    {item.description ? item.description : <span className="text-gray-400 italic">e.g: ARK - Concept Artist</span>}
                  </div>
                  {!pdfMode && item.source === 'auto' && (
                    <div className="flex gap-1 pdf-exclude mt-0.5 pl-4">
                      <Badge variant="outline" className="text-xs py-0 h-3.5 bg-blue-50 text-blue-700 border-blue-200">
                        <Zap className="h-2 w-2 mr-0.5" /> Auto
                      </Badge>
                      {item.modified && (
                        <Badge variant="outline" className="text-xs py-0 h-3.5 bg-amber-50 text-amber-700 border-amber-200">
                          <FileEdit className="h-2 w-2 mr-0.5" /> Modified
                        </Badge>
                      )}
                    </div>
                  )}
                </div>}
              </TableCell>
              <TableCell className="py-2 px-4 text-right">
                {editingCell?.id === item.id && editingCell?.field === "quantity" ? <Input ref={inputRef} value={editValue} onChange={e => setEditValue(e.target.value)} className="h-7 text-right w-full" type="number" min="0.01" step="0.01" onKeyDown={e => {
              if (e.key === "Enter") handleSave(item.id, "quantity", editValue);
              if (e.key === "Escape") cancelEditing();
              handleTabNavigation(e, item.id, "quantity");
            }} onBlur={() => handleSave(item.id, "quantity", editValue)} autoFocus /> : <div className={`${!readOnly && !pdfMode ? "cursor-pointer hover:bg-accent hover:text-accent-foreground" : ""} p-2 rounded min-h-[2rem] flex items-center justify-end`} onClick={() => !pdfMode && startEditing(item.id, "quantity", item.quantity)}>
                    {item.quantity || 1}
                  </div>}
              </TableCell>
              <TableCell className="py-2 px-4 text-right">
                {editingCell?.id === item.id && editingCell?.field === "rate" ? <Input ref={inputRef} value={editValue} onChange={e => setEditValue(e.target.value)} className="h-7 text-right w-full" type="number" min="0.01" step="0.01" onKeyDown={e => {
              if (e.key === "Enter") handleSave(item.id, "rate", editValue);
              if (e.key === "Escape") cancelEditing();
              handleTabNavigation(e, item.id, "rate");
            }} onBlur={() => handleSave(item.id, "rate", editValue)} autoFocus /> : <div className={`${!readOnly && !pdfMode ? "cursor-pointer hover:bg-accent hover:text-accent-foreground" : ""} p-2 rounded min-h-[2rem] flex items-center justify-end`} onClick={() => !pdfMode && startEditing(item.id, "rate", item.rate)}>
                    {formatCurrency(item.rate)}
                  </div>}
              </TableCell>
              <TableCell className="py-2 px-4 text-right">
                <div className="p-2 min-h-[2rem] flex items-center justify-end">
                  {formatCurrency(item.amount)}
                </div>
              </TableCell>
              {!readOnly && !pdfMode && <TableCell className="p-0 w-10">
                  <Button variant="ghost" size="icon" onClick={() => onDelete(item.id)} className="h-7 w-7 pdf-exclude" disabled={items.length <= 1}>
                    <Trash2 className="h-4 w-4 text-red-500" />
                  </Button>
                </TableCell>}
            </TableRow>)}
        </TableBody>
        <TableFooter>
          <TableRow>
            {!readOnly && !pdfMode && <TableCell colSpan={1} className="py-2 px-4">
                <Button variant="ghost" size="icon" onClick={handleAddItem} className="text-gray-500 hover:text-primary h-7 pl-0 pdf-exclude">
                  <Plus className="h-4 w-4" />
                </Button>
              </TableCell>}
            <TableCell colSpan={!readOnly && !pdfMode ? 2 : 3} className="py-2 px-4 text-right font-bold">
              Total
            </TableCell>
            <TableCell className="py-2 px-4 text-right font-bold">
              {formatCurrency(calculateTotal())}
            </TableCell>
            {!readOnly && !pdfMode && <TableCell className="w-10"></TableCell>}
          </TableRow>
        </TableFooter>
      </Table>
    </div>;
}
